import { IPayHistoryChangeEntry } from 'typings/payHistory';
import { IAmount } from 'typings/amount';
import { Amount } from 'nomst/Stores/Decision/amount';
import { TeamPageViews } from 'nomst/Stores/Configuration/teamPageViews';
import { EmployeeClass, AmountCode, StockDisplayType, AmountPayType, PercentageCalculationRule } from 'typings/enums';
import AmountConvert from 'common/amountConvert';
import { currencyConversion } from 'utils/MobxDataManager/singletons';
import { defaultWeeksPerYear } from 'common/constants';

export class PayHistoryChangeEntry implements IPayHistoryChangeEntry {
    payHistoryChangeEntryId: number;
    payHistoryId: number;
    payEffectiveDate: string;
    countryCode: string;
    isoCurrencyCode: string;
    homeIsoCurrencyCode: string;
    weeklyStandardWorkHours: number;
    employeeClass: EmployeeClass;
    baseSalary: Nullable<number>;
    hourlyRate: Nullable<number>;
    meritIncreaseProrateRatio: Nullable<number>;
    marketAdjustmentProrateRatio: Nullable<number>;
    bonusProrateRatio: Nullable<number>;
    lumpSumProrateRatio: Nullable<number>;
    promotionProrateRatio: Nullable<number>;
    variablePayProrateRatio: Nullable<number>;
    currentTitle: Nullable<string>;
    newTitle: Nullable<string>;
    meritIncrease: Nullable<number>;
    marketAdjustment: Nullable<number>;
    bonus: Nullable<number>;
    lumpSum: Nullable<number>;
    promotion: Nullable<number>;
    variablePay: Nullable<number>;
    stockPay: Nullable<number>;
    stockUnitType: Nullable<StockDisplayType>;
    stockPrice: Nullable<number>;
    amounts: IAmount[];
    private _employeeSourceId: string;
    private _teamPageViews: TeamPageViews;

    private basePayAmountCodes: Set<AmountCode> = new Set<AmountCode>([
        AmountCode.Merit,
        AmountCode.MarketAdjustment,
        AmountCode.Promotion
    ]);

    constructor(entryDto: IPayHistoryChangeEntry, employeeSourceId: string, teamPageViews: TeamPageViews) {
        this.payHistoryChangeEntryId = entryDto.payHistoryChangeEntryId;
        this.payHistoryId = entryDto.payHistoryId;
        this.payEffectiveDate = entryDto.payEffectiveDate;
        this.countryCode = entryDto.countryCode;
        this.isoCurrencyCode = entryDto.isoCurrencyCode;
        this.homeIsoCurrencyCode = entryDto.homeIsoCurrencyCode;
        this.weeklyStandardWorkHours = entryDto.weeklyStandardWorkHours;
        this.employeeClass = entryDto.employeeClass;
        this.baseSalary = entryDto.baseSalary;
        this.hourlyRate = entryDto.hourlyRate;
        this.meritIncreaseProrateRatio = entryDto.meritIncreaseProrateRatio;
        this.marketAdjustmentProrateRatio = entryDto.marketAdjustmentProrateRatio;
        this.bonusProrateRatio = entryDto.bonusProrateRatio;
        this.lumpSumProrateRatio = entryDto.lumpSumProrateRatio;
        this.promotionProrateRatio = entryDto.promotionProrateRatio;
        this.variablePayProrateRatio = entryDto.variablePayProrateRatio;
        this.currentTitle = entryDto.currentTitle;
        this.newTitle = entryDto.newTitle;
        this.meritIncrease = entryDto.meritIncrease;
        this.marketAdjustment = entryDto.marketAdjustment;
        this.bonus = entryDto.bonus;
        this.lumpSum = entryDto.lumpSum;
        this.promotion = entryDto.promotion;
        this.variablePay = entryDto.variablePay;
        this.stockPay = entryDto.stockPay;
        this.stockUnitType = entryDto.stockUnitType;
        this.stockPrice = entryDto.stockPrice;
        this._employeeSourceId = employeeSourceId;
        this._teamPageViews = teamPageViews;

        this.initializeAmounts();
    }

    get isHourly(): boolean {
        return this.employeeClass === EmployeeClass.Hourly;
    }

    get annualStandardWorkHours(): number {
        return this.weeklyStandardWorkHours * defaultWeeksPerYear;
    }

    get payEffectiveDateType(): Date {
        return new Date(this.payEffectiveDate);
    }

    get previousBasePay(): Nullable<number> {
        return this.isHourly ? this.hourlyRate : this.baseSalary;
    }

    get annualPreviousBasePay(): number {
        const previousBasePay = this.previousBasePay || 0;
        return AmountConvert.convertToAnnual(previousBasePay, this.annualStandardWorkHours, this.isHourly);
    }

    get basePay(): number {
        const previousBasePay = this.previousBasePay || 0;
        return previousBasePay + this.amounts.filter(amount => amount.updateNewPay).reduce<number>(this.sum, 0);
    }

    get annualBasePay(): number {
        return AmountConvert.convertToAnnual(this.basePay, this.annualStandardWorkHours, this.isHourly);
    }

    get tcc(): number {
        return this.annualBasePay + this.amounts.filter(amount => amount.updateTcc).reduce<number>(this.sum, 0);
    }

    initializeAmounts(): void {
        this.amounts = [];
        if (this.meritIncrease) {
            this.addAmount(this.meritIncrease, AmountCode.Merit, this.meritIncreaseProrateRatio);
        }

        if (this.bonus) {
            this.addAmount(this.bonus, AmountCode.Bonus, this.bonusProrateRatio);
        }

        if (this.marketAdjustment) {
            this.addAmount(this.marketAdjustment, AmountCode.MarketAdjustment, this.marketAdjustmentProrateRatio);
        }

        if (this.lumpSum) {
            this.addAmount(this.lumpSum, AmountCode.LumpSum, this.lumpSumProrateRatio);
        }

        if (this.promotion) {
            this.addAmount(this.promotion, AmountCode.Promotion, this.promotionProrateRatio);
        }

        if (this.variablePay) {
            this.addAmount(this.variablePay, AmountCode.VariablePay, this.variablePayProrateRatio);
        }

        if (this.stockPay) {
            this.addAmount(this.stockPay, AmountCode.Stock, 1);
        }
    }

    private sum(accumulator: number, amount: IAmount): number {
        return accumulator + (amount.value || 0);
    }

    private addAmount(value: number, amountCode: AmountCode, prorateRatio: Nullable<number>): void {
        const isBasePay = this.basePayAmountCodes.has(amountCode);
        const updateNewPay = this.basePayAmountCodes.has(amountCode);
        const amountDto = {
            value,
            amountCode,
            prorateRatio,
            amountIsoCurrencyCode: this.isoCurrencyCode,
            currentIsoCurrencyCode: this.homeIsoCurrencyCode,
            updateTcc: !updateNewPay && this._teamPageViews.doesAmountUseTccField(amountCode)
        } as IAmount;
        const payType = isBasePay ? AmountPayType.BasePay : AmountPayType.None;
        let previousPay = 0;
        if (this.isHourly && updateNewPay) {
            previousPay = this.previousBasePay || 0;
        } else {
            previousPay = this.annualPreviousBasePay;
        }
        const amount = new Amount(
            amountDto,
            this._employeeSourceId,
            this.basePay,
            previousPay,
            PercentageCalculationRule.PercentageOfTarget,
            payType,
            this.isoCurrencyCode,
            this.homeIsoCurrencyCode,
            currencyConversion,
            null
        );

        this.amounts.push(amount);
    }
}